import Coach from '../../Model/Coach';
import { ClientStatus, filterChecks } from './statusFilter';

/**
 * Whether a client is live workload for their coach: active, and not paused.
 *
 * Clients whose service has not started yet count — they become workload within days of
 * subscribing. `ON_BREAK` already excludes them, so it is the right check here where `ACTIVE` is
 * not.
 * @param {User} user - A client document scoped to the coach being measured.
 * @return {boolean} True when the client is active and not paused.
 */
const isActiveUnpausedClient = (user) => !!user.isActive && !filterChecks[ClientStatus.ON_BREAK](user);

/**
 * Whether a client occupies a slot in their AC's pod.
 *
 * Paused clients keep their pod assignment but stop consuming capacity, which is what makes a pod's
 * assignment total read higher than its real load. That is the same population as
 * `isActiveUnpausedClient`, kept under its own name because pod capacity is the question being asked
 * here and it is free to diverge from plain workload later.
 * @param {User} user - A client document scoped to the coach whose pods are being measured.
 * @return {boolean} True when the client counts towards their pod's capacity.
 */
const consumesPodCapacity = (user) => isActiveUnpausedClient(user);

/**
 * Client counts for a coach's pods, split into the clients that consume capacity and every client
 * assigned to a pod. Both numbers come from one query — capacity decisions need the former, while
 * the latter explains why a pod holds more people than its capacity figure suggests.
 *
 * Only clients who are actually in a pod are counted, so each total is exactly the sum of its
 * per-pod breakdown and the table's columns reconcile with the figures above them. `assignedByPod`
 * is also the same measure the server's rebalance writes to `coachPod.userCount` — that counter
 * drifts between rebalances, this does not.
 * @param {string} coachId - The coach whose pods are being measured.
 * @return {Promise<{
 *   activeTotal: number,
 *   assignedTotal: number,
 *   activeByPod: Object<string, number>,
 *   assignedByPod: Object<string, number>,
 * }>} Totals across the coach's pods, and per-pod breakdowns keyed by pod id.
 */
const getPodClientCounts = async (coachId) => {
  const clients = await Coach.getInPodClients(coachId);

  const activeByPod = {};
  const assignedByPod = {};
  let activeTotal = 0;
  let assignedTotal = 0;

  clients.forEach((client) => {
    const podId = client.userPod;
    if (!podId) {
      return;
    }

    assignedTotal += 1;
    assignedByPod[podId] = (assignedByPod[podId] || 0) + 1;

    if (consumesPodCapacity(client)) {
      activeTotal += 1;
      activeByPod[podId] = (activeByPod[podId] || 0) + 1;
    }
  });

  return {
    activeTotal,
    assignedTotal,
    activeByPod,
    assignedByPod,
  };
};

export {
  isActiveUnpausedClient,
  consumesPodCapacity,
  getPodClientCounts,
};
